import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { campaignsAPI, leadsAPI, emailTemplatesAPI } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/stores/auth-store";
import { useTeams } from "@/hooks/use-teams";
import { Campaign, CampaignStatus, CampaignsResponse, CampaignType, GetMatchingLeadsData, MatchingLead } from "@/types/campaigns";
import { EmailTemplate, EMAIL_TEMPLATE_TYPES, EmailTemplateType } from "@/types/email-templates";
import {
  Mail,
  Plus,
  Send,
  Users,
  Search,
  Calendar,
  BarChart3,
  Pause,
  X,
  Target,
  Clock,
  CheckCircle,
  AlertCircle,
  MoreHorizontal,
  Zap,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";

export function CampaignsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [newCampaign, setNewCampaign] = useState({
    name: "",
    description: "",
    campaignType: CampaignType.WEB_DEVELOPMENT,
    targetIndustries: [] as string[],
    targetLeadIds: [] as number[],
    templateId: undefined as number | undefined,
    customSubject: "",
    customContent: "",
    aiPersonalization: true,
    emailType: "cold_outreach" as EmailTemplateType,
  });
  const [matchingLeads, setMatchingLeads] = useState<MatchingLead[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const { currentTeam, isLoading: teamsLoading, hasTeams, getCurrentTeamId } = useTeams();

  const teamId = getCurrentTeamId();

  const {
    data: campaignsResponse,
    isLoading: campaignsLoading,
    error,
  } = useQuery({
    queryKey: ["campaigns", searchTerm, statusFilter],
    queryFn: () => {
      const params: any = { limit: 20 };
      if (searchTerm) params.search = searchTerm;
      if (statusFilter !== "all") params.status = statusFilter;
      return campaignsAPI.getCampaigns(params);
    },
    enabled: !!teamId, // Only run query if we have a teamId
  });

  const { data: leadsResponse } = useQuery({
    queryKey: ["leads-for-campaigns", teamId],
    queryFn: () => leadsAPI.getLeads(teamId!, { limit: 100 }),
    enabled: !!teamId, // Only run query if we have a teamId
  });

  const { data: templatesResponse } = useQuery({
    queryKey: ["email-templates", teamId, newCampaign.emailType],
    queryFn: () => emailTemplatesAPI.getTemplates(teamId!, { type: newCampaign.emailType }),
    enabled: !!teamId, // Only run query if we have a teamId
  });

  const isLoading = teamsLoading || campaignsLoading;

  const createCampaignMutation = useMutation({
    mutationFn: campaignsAPI.createCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      setCreateModalOpen(false);
      setNewCampaign({
        name: "",
        description: "",
        campaignType: CampaignType.WEB_DEVELOPMENT,
        targetIndustries: [],
        targetLeadIds: [],
        templateId: undefined,
        customSubject: "",
        customContent: "",
        aiPersonalization: true,
        emailType: "cold_outreach" as EmailTemplateType,
      });
      setMatchingLeads([]);
      setCurrentStep(1);
      toast({
        title: "Campaign Created",
        description: "Your campaign has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to create campaign",
        variant: "destructive",
      });
    },
  });

  const launchCampaignMutation = useMutation({
    mutationFn: campaignsAPI.launchCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: "Campaign Launched",
        description: "Your campaign is now sending emails to your leads.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Launch Failed",
        description: error.response?.data?.message || "Failed to launch campaign",
        variant: "destructive",
      });
    },
  });

  const pauseCampaignMutation = useMutation({
    mutationFn: campaignsAPI.pauseCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: "Campaign Paused",
        description: "Your campaign has been paused.",
      });
    },
  });

  const resumeCampaignMutation = useMutation({
    mutationFn: campaignsAPI.startCampaign,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaigns"] });
      toast({
        title: "Campaign Resumed",
        description: "Your campaign has been resumed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to resume campaign",
        variant: "destructive",
      });
    },
  });

  const generateContentMutation = useMutation({
    mutationFn: (data: any) => emailTemplatesAPI.generateContent(teamId!, data),
    onSuccess: (response: any) => {
      const generated = response.data;
      setNewCampaign(prev => ({
        ...prev,
        customSubject: generated.subject,
        customContent: generated.content,
      }));
      setGeneratingContent(false);
      toast({
        title: "Content Generated",
        description: "AI has generated personalized email content for your campaign.",
      });
    },
    onError: (error: any) => {
      setGeneratingContent(false);
      toast({
        title: "Generation Failed",
        description: error.response?.data?.message || "Failed to generate content",
        variant: "destructive",
      });
    },
  });

  const fetchMatchingLeads = async () => {
    if (!teamId) return;
    
    setLoadingMatches(true);
    try {
      const matchingData: GetMatchingLeadsData = {
        campaignType: newCampaign.campaignType,
        ...(newCampaign.targetIndustries.length > 0 && { targetIndustries: newCampaign.targetIndustries }),
        minMatchScore: 30,
        limit: 50,
      };

      const response = await campaignsAPI.getMatchingLeads(matchingData);
      const matchingLeadsData = response.data as MatchingLead[];
      setMatchingLeads(matchingLeadsData);
      
      toast({
        title: "Matching Leads Found",
        description: `Found ${matchingLeadsData.length} leads that match your campaign criteria.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to find matching leads",
        variant: "destructive",
      });
    } finally {
      setLoadingMatches(false);
    }
  };

  const handleGenerateContent = () => {
    if (newCampaign.targetLeadIds.length === 0) {
      toast({
        title: "Select Leads First",
        description: "Please select at least one lead to generate personalized content.",
        variant: "destructive",
      });
      return;
    }

    setGeneratingContent(true);
    
    // Get first lead's data for AI generation
    const firstLead = leads.find((lead: any) => lead.id === newCampaign.targetLeadIds[0]);
    
    const generateData = {
      templateId: newCampaign.templateId,
      type: newCampaign.emailType,
      leadData: {
        name: firstLead?.name,
        company: firstLead?.company,
        industry: firstLead?.industry,
        jobTitle: firstLead?.jobTitle,
        website: firstLead?.website,
        location: firstLead?.location,
      },
      senderData: {
        name: user?.name || "Your Name",
        company: currentTeam?.name || "Your Company",
        industry: currentTeam?.industry,
        services: [], // TODO: Get from team profile
      },
      context: newCampaign.description,
    };

    generateContentMutation.mutate(generateData);
  };

  const steps = [
    { number: 1, title: "Campaign Details", description: "Basic information" },
    { number: 2, title: "Service & Targeting", description: "Campaign type and audience" },
    { number: 3, title: "Lead Selection", description: "Choose your leads" },
    { number: 4, title: "Email Content", description: "Create your message" },
  ];

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return newCampaign.name.trim() !== "";
      case 2:
        return newCampaign.campaignType !== null && newCampaign.campaignType !== undefined;
      case 3:
        return newCampaign.targetLeadIds.length > 0;
      case 4:
        return newCampaign.customSubject.trim() !== "" && newCampaign.customContent.trim() !== "";
      default:
        return true;
    }
  };

  const handleNextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
    } else {
      let message = "";
      switch (currentStep) {
        case 1:
          message = "Campaign name is required";
          break;
        case 2:
          message = "Please select a campaign type";
          break;
        case 3:
          message = "Please select at least one lead";
          break;
        case 4:
          message = "Please add both subject and content";
          break;
      }
      toast({
        title: "Validation Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const canProceedToStep = (step: number): boolean => {
    for (let i = 1; i < step; i++) {
      if (!validateStep(i)) return false;
    }
    return true;
  };

  const handleCreateCampaign = () => {
    const campaignData: any = {
      name: newCampaign.name,
      description: newCampaign.description,
      campaignType: newCampaign.campaignType,
      targetIndustries: newCampaign.targetIndustries,
      targetLeadIds: newCampaign.targetLeadIds,
      aiPersonalization: newCampaign.aiPersonalization,
    };

    if (newCampaign.templateId) {
      campaignData.templateId = newCampaign.templateId;
    }

    if (newCampaign.customSubject) {
      campaignData.customSubject = newCampaign.customSubject;
    }

    if (newCampaign.customContent) {
      campaignData.customContent = newCampaign.customContent;
    }

    createCampaignMutation.mutate(campaignData);
  };

  const campaignsData = campaignsResponse?.data as
    | CampaignsResponse
    | undefined;
  const campaigns = campaignsData?.campaigns || [];
  const leadsData = leadsResponse?.data as any;
  // Handle the nested response structure: data.data contains the leads array
  const leads = leadsData?.data || [];
  const templatesData = templatesResponse?.data as EmailTemplate[] | undefined;
  const templates = templatesData || [];

  const getStatusIcon = (status: CampaignStatus) => {
    switch (status) {
      case CampaignStatus.DRAFT:
        return <Clock className="h-4 w-4" />;
      case CampaignStatus.SCHEDULED:
        return <Calendar className="h-4 w-4" />;
      case CampaignStatus.SENDING:
        return <Send className="h-4 w-4" />;
      case CampaignStatus.SENT:
        return <CheckCircle className="h-4 w-4" />;
      case CampaignStatus.PAUSED:
        return <Pause className="h-4 w-4" />;
      case CampaignStatus.CANCELLED:
        return <X className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getStatusColor = (status: CampaignStatus) => {
    switch (status) {
      case CampaignStatus.DRAFT:
        return "bg-gray-100 text-gray-800";
      case CampaignStatus.SCHEDULED:
        return "bg-blue-100 text-blue-800";
      case CampaignStatus.SENDING:
        return "bg-yellow-100 text-yellow-800";
      case CampaignStatus.SENT:
        return "bg-green-100 text-green-800";
      case CampaignStatus.PAUSED:
        return "bg-orange-100 text-orange-800";
      case CampaignStatus.CANCELLED:
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Email Campaigns</h1>
            <p className="text-muted-foreground">
              Create and manage your outreach campaigns
            </p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (!teamsLoading && !hasTeams) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Email Campaigns</h1>
            <p className="text-muted-foreground">
              Create and manage your outreach campaigns
            </p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertDescription>
            No teams found. You need to be a member of a team to manage campaigns.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Email Campaigns</h1>
            <p className="text-muted-foreground">
              Create and manage your outreach campaigns
            </p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load campaigns: {(error as Error).message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Email Campaigns</h1>
          <p className="text-muted-foreground">
            Create and manage your outreach campaigns
          </p>
        </div>
        <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle>Create New Campaign</DialogTitle>
            </DialogHeader>

            {/* Stepper Header */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between">
                {steps.map((step, index) => (
                  <div key={step.number} className="flex items-center">
                    <div
                      className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                        currentStep === step.number
                          ? "border-blue-600 bg-blue-600 text-white"
                          : currentStep > step.number
                          ? "border-green-600 bg-green-600 text-white"
                          : canProceedToStep(step.number)
                          ? "border-gray-300 bg-white text-gray-900 cursor-pointer hover:border-blue-600"
                          : "border-gray-200 bg-gray-100 text-gray-400"
                      }`}
                      onClick={() => canProceedToStep(step.number) && setCurrentStep(step.number)}
                    >
                      {currentStep > step.number ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        step.number
                      )}
                    </div>
                    <div className="ml-3 text-left">
                      <div className={`text-sm font-medium ${currentStep >= step.number ? "text-gray-900" : "text-gray-400"}`}>
                        {step.title}
                      </div>
                      <div className={`text-xs ${currentStep >= step.number ? "text-gray-500" : "text-gray-300"}`}>
                        {step.description}
                      </div>
                    </div>
                    {index < steps.length - 1 && (
                      <ChevronRight className="w-4 h-4 text-gray-400 ml-4" />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Step Content */}
            <div className="flex-1 overflow-y-auto py-6">
              {currentStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Campaign Details</h3>
                  <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Campaign Name</Label>
                  <Input
                    id="name"
                    value={newCampaign.name}
                    onChange={(e) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Enter campaign name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newCampaign.description}
                    onChange={(e) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        description: e.target.value,
                      }))
                    }
                    placeholder="Describe your campaign"
                    rows={3}
                  />
                </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Service & Targeting</h3>
                  <div className="grid grid-cols-1 gap-4">
                {/* Campaign Type Selection */}
                <div className="space-y-2">
                  <Label htmlFor="campaignType">Campaign Type</Label>
                  <Select 
                    value={newCampaign.campaignType} 
                    onValueChange={(value: CampaignType) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        campaignType: value,
                        templateId: undefined, // Reset template when type changes
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select campaign type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CampaignType.WEB_DEVELOPMENT}>Web Development</SelectItem>
                      <SelectItem value={CampaignType.MOBILE_DEVELOPMENT}>Mobile Development</SelectItem>
                      <SelectItem value={CampaignType.SAAS_DEVELOPMENT}>SaaS Development</SelectItem>
                      <SelectItem value={CampaignType.E_COMMERCE}>E-commerce</SelectItem>
                      <SelectItem value={CampaignType.STAFF_AUGMENTATION}>Staff Augmentation</SelectItem>
                      <SelectItem value={CampaignType.CONSULTING}>Consulting</SelectItem>
                      <SelectItem value={CampaignType.DESIGN_SERVICES}>Design Services</SelectItem>
                      <SelectItem value={CampaignType.DEVOPS}>DevOps</SelectItem>
                      <SelectItem value={CampaignType.DATA_ANALYTICS}>Data Analytics</SelectItem>
                      <SelectItem value={CampaignType.AI_ML}>AI/ML</SelectItem>
                      <SelectItem value={CampaignType.CUSTOM}>Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Target Industries */}
                <div className="space-y-2">
                  <Label htmlFor="targetIndustries">Target Industries (Optional)</Label>
                  <Input
                    id="targetIndustries"
                    value={newCampaign.targetIndustries.join(', ')}
                    onChange={(e) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        targetIndustries: e.target.value.split(',').map(i => i.trim()).filter(i => i),
                      }))
                    }
                    placeholder="e.g., Technology, Healthcare, Finance (comma-separated)"
                  />
                </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Lead Selection</h3>
                
                {/* Email Type Selection */}
                <div className="space-y-2">
                  <Label htmlFor="emailType">Email Type</Label>
                  <Select 
                    value={newCampaign.emailType} 
                    onValueChange={(value: EmailTemplateType) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        emailType: value,
                        templateId: undefined, // Reset template when type changes
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select email type" />
                    </SelectTrigger>
                    <SelectContent>
                      {EMAIL_TEMPLATE_TYPES.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Template Selection */}
                <div className="space-y-2">
                  <Label htmlFor="template">Email Template (Optional)</Label>
                  <Select 
                    value={newCampaign.templateId?.toString() || "none"} 
                    onValueChange={(value) =>
                      setNewCampaign((prev) => ({
                        ...prev,
                        templateId: value === "none" ? undefined : parseInt(value),
                      }))
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a template or create custom content" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No template (Custom content)</SelectItem>
                      {templates.map((template) => (
                        <SelectItem key={template.id} value={template.id.toString()}>
                          {template.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Custom Email Content */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>Email Content</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateContent}
                      disabled={generatingContent || generateContentMutation.isPending}
                    >
                      {(generatingContent || generateContentMutation.isPending) ? (
                        <>
                          <AlertCircle className="h-3 w-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Zap className="h-3 w-3 mr-1" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="customSubject">Subject Line</Label>
                    <Input
                      id="customSubject"
                      value={newCampaign.customSubject}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          customSubject: e.target.value,
                        }))
                      }
                      placeholder="Enter email subject"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="customContent">Email Body</Label>
                    <Textarea
                      id="customContent"
                      value={newCampaign.customContent}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          customContent: e.target.value,
                        }))
                      }
                      placeholder="Enter email content or generate with AI"
                      rows={6}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>Select Leads</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={fetchMatchingLeads}
                      disabled={loadingMatches}
                    >
                      {loadingMatches ? (
                        <>
                          <AlertCircle className="h-3 w-3 mr-1 animate-spin" />
                          Finding Matches...
                        </>
                      ) : (
                        <>
                          <Target className="h-3 w-3 mr-1" />
                          Find Matching Leads
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Show matching leads if available */}
                  {matchingLeads.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          Smart Matches ({matchingLeads.length})
                        </Badge>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const matchingIds = matchingLeads.map(lead => lead.id);
                            setNewCampaign(prev => ({
                              ...prev,
                              targetLeadIds: [...new Set([...prev.targetLeadIds, ...matchingIds])]
                            }));
                          }}
                        >
                          Select All Matches
                        </Button>
                      </div>
                      <div className="border rounded-md p-4 max-h-64 overflow-y-auto bg-green-50">
                        {matchingLeads.map((lead) => (
                          <div key={lead.id} className="border-b last:border-b-0 py-3">
                            <div className="flex items-start space-x-2">
                              <input
                                type="checkbox"
                                id={`match-${lead.id}`}
                                checked={newCampaign.targetLeadIds.includes(lead.id)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setNewCampaign((prev) => ({
                                    ...prev,
                                    targetLeadIds: checked
                                      ? [...prev.targetLeadIds, lead.id]
                                      : prev.targetLeadIds.filter((id) => id !== lead.id),
                                  }));
                                }}
                                className="rounded mt-1"
                              />
                              <div className="flex-1">
                                <label htmlFor={`match-${lead.id}`} className="block text-sm">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{lead.name}</span>
                                    <Badge variant="outline" className="text-xs bg-green-100">
                                      {lead.matchScore}% match
                                    </Badge>
                                  </div>
                                  <div className="text-muted-foreground">
                                    {lead.email} • {lead.company} • {lead.industry}
                                  </div>
                                  {lead.tags.length > 0 && (
                                    <div className="flex gap-1 mt-1">
                                      {lead.tags.map((tag, index) => (
                                        <Badge key={index} variant="secondary" className="text-xs">
                                          {tag}
                                        </Badge>
                                      ))}
                                    </div>
                                  )}
                                  <div className="text-xs text-green-700 mt-1">
                                    <strong>Why matched:</strong> {lead.matchReasons.join(', ')}
                                  </div>
                                </label>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* All leads section */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">All Leads</Label>
                    <div className="border rounded-md p-4 max-h-48 overflow-y-auto">
                    {leads.length > 0 ? (
                      leads.map((lead: any) => (
                        <div
                          key={lead.id}
                          className="flex items-center space-x-2 py-2"
                        >
                          <input
                            type="checkbox"
                            id={`lead-${lead.id}`}
                            checked={newCampaign.targetLeadIds.includes(
                              lead.id
                            )}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setNewCampaign((prev) => ({
                                ...prev,
                                targetLeadIds: checked
                                  ? [...prev.targetLeadIds, lead.id]
                                  : prev.targetLeadIds.filter(
                                      (id) => id !== lead.id
                                    ),
                              }));
                            }}
                            className="rounded"
                          />
                          <label
                            htmlFor={`lead-${lead.id}`}
                            className="flex-1 text-sm"
                          >
                            <span className="font-medium">{lead.name}</span>
                            <span className="text-muted-foreground ml-2">
                              ({lead.email})
                            </span>
                            {lead.company && (
                              <span className="text-muted-foreground ml-2">
                                - {lead.company}
                              </span>
                            )}
                          </label>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted-foreground text-center py-4">
                        No leads available
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {newCampaign.targetLeadIds.length} lead(s) selected
                  </p>
                  </div>
                </div>
                </div>
              )}

              {currentStep === 4 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium">Email Content</h3>
                  
                {/* Custom Email Content */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <Label>Email Content</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateContent}
                      disabled={generatingContent || generateContentMutation.isPending}
                    >
                      {(generatingContent || generateContentMutation.isPending) ? (
                        <>
                          <AlertCircle className="h-3 w-3 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Zap className="h-3 w-3 mr-1" />
                          Generate with AI
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="customSubject">Subject Line</Label>
                    <Input
                      id="customSubject"
                      value={newCampaign.customSubject}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          customSubject: e.target.value,
                        }))
                      }
                      placeholder="Enter email subject"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="customContent">Email Body</Label>
                    <Textarea
                      id="customContent"
                      value={newCampaign.customContent}
                      onChange={(e) =>
                        setNewCampaign((prev) => ({
                          ...prev,
                          customContent: e.target.value,
                        }))
                      }
                      placeholder="Enter email content or generate with AI"
                      rows={8}
                    />
                  </div>
                </div>
                </div>
              )}
            </div>

            {/* Navigation Footer */}
            <div className="border-t pt-4 flex justify-between items-center">
              <Button
                variant="outline"
                onClick={currentStep === 1 ? () => setCreateModalOpen(false) : handlePrevStep}
              >
                {currentStep === 1 ? (
                  <>
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </>
                ) : (
                  <>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Previous
                  </>
                )}
              </Button>

              <div className="text-sm text-muted-foreground">
                Step {currentStep} of {steps.length}
              </div>

              {currentStep < steps.length ? (
                <Button onClick={handleNextStep}>
                  Next
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleCreateCampaign}
                  disabled={createCampaignMutation.isPending || !validateStep(4)}
                >
                  {createCampaignMutation.isPending ? (
                    <>
                      <AlertCircle className="h-3 w-3 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Create Campaign
                    </>
                  )}
                </Button>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Mail className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Campaigns
                </p>
                <p className="text-2xl font-bold">
                  {campaignsData?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Send className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Active
                </p>
                <p className="text-2xl font-bold">
                  {
                    campaigns.filter(
                      (c) =>
                        c.status === CampaignStatus.SENDING ||
                        c.status === CampaignStatus.SCHEDULED
                    ).length
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Target className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Total Leads
                </p>
                <p className="text-2xl font-bold">
                  {campaigns.reduce((sum, c) => sum + c.leadCount, 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <BarChart3 className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-muted-foreground">
                  Avg Open Rate
                </p>
                <p className="text-2xl font-bold">
                  {campaigns.length > 0
                    ? Math.round(
                        campaigns.reduce(
                          (sum, c) => sum + (c.stats?.openRate || 0),
                          0
                        ) / campaigns.length
                      )
                    : 0}
                  %
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="scheduled">Scheduled</SelectItem>
                <SelectItem value="sending">Sending</SelectItem>
                <SelectItem value="sent">Completed</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Campaigns List */}
      <Card>
        <CardHeader>
          <CardTitle>Campaigns ({campaignsData?.total || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="font-medium">{campaign.name}</h3>
                      <Badge variant="outline" className="text-xs">
                        {campaign.campaignType?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Campaign'}
                      </Badge>
                      <Badge
                        className={`${getStatusColor(campaign.status)} flex items-center gap-1`}
                      >
                        {getStatusIcon(campaign.status)}
                        {campaign.status.charAt(0).toUpperCase() +
                          campaign.status.slice(1)}
                      </Badge>
                      {campaign.aiPersonalization && (
                        <Badge variant="outline" className="text-xs">
                          AI Personalized
                        </Badge>
                      )}
                    </div>

                    {campaign.description && (
                      <p className="text-sm text-muted-foreground mb-2">
                        {campaign.description}
                      </p>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center">
                        <Target className="h-3 w-3 mr-1" />
                        {campaign.leadCount} leads
                      </div>
                      {campaign.stats && (
                        <>
                          <div>Sent: {campaign.stats.emailsSent}</div>
                          <div>
                            Opened: {campaign.stats.emailsOpened} (
                            {campaign.stats.openRate.toFixed(1)}%)
                          </div>
                          <div>
                            Replied: {campaign.stats.emailsReplied} (
                            {campaign.stats.replyRate.toFixed(1)}%)
                          </div>
                        </>
                      )}
                      <div className="flex items-center">
                        <Calendar className="h-3 w-3 mr-1" />
                        {new Date(campaign.createdAt).toLocaleDateString()}
                      </div>
                    </div>

                    {campaign.scheduledFor && (
                      <div className="mt-2 text-sm text-blue-600">
                        Scheduled for:{" "}
                        {new Date(campaign.scheduledFor).toLocaleDateString()}{" "}
                        at{" "}
                        {new Date(campaign.scheduledFor).toLocaleTimeString()}
                      </div>
                    )}
                  </div>

                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setSelectedCampaign(campaign);
                        setViewModalOpen(true);
                      }}
                    >
                      <BarChart3 className="h-3 w-3 mr-1" />
                      View
                    </Button>

                    {campaign.status === CampaignStatus.DRAFT && (
                      <Button
                        size="sm"
                        onClick={() =>
                          launchCampaignMutation.mutate(campaign.id)
                        }
                        disabled={launchCampaignMutation.isPending}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        {launchCampaignMutation.isPending ? "Launching..." : "Launch"}
                      </Button>
                    )}

                    {(campaign.status === CampaignStatus.SENDING ||
                      campaign.status === CampaignStatus.SCHEDULED) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          pauseCampaignMutation.mutate(campaign.id)
                        }
                        disabled={pauseCampaignMutation.isPending}
                      >
                        <Pause className="h-3 w-3 mr-1" />
                        Pause
                      </Button>
                    )}

                    {campaign.status === CampaignStatus.PAUSED && (
                      <Button
                        size="sm"
                        onClick={() =>
                          resumeCampaignMutation.mutate(campaign.id)
                        }
                        disabled={resumeCampaignMutation.isPending}
                      >
                        <Send className="h-3 w-3 mr-1" />
                        Resume
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {campaigns.length === 0 && (
              <div className="text-center py-8">
                <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No campaigns found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || statusFilter !== "all"
                    ? "Try adjusting your filters or search terms"
                    : "Create your first email campaign to start reaching out to leads"}
                </p>
                {!searchTerm && statusFilter === "all" && (
                  <Button onClick={() => setCreateModalOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Campaign
                  </Button>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* View Campaign Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Campaign Details: {selectedCampaign?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedCampaign && (
            <div className="space-y-6">
              {/* Campaign Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Campaign Information</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">Status:</span> 
                      <Badge className="ml-2" variant={
                        selectedCampaign.status === CampaignStatus.SENT ? "default" :
                        selectedCampaign.status === CampaignStatus.SENDING ? "secondary" :
                        selectedCampaign.status === CampaignStatus.DRAFT ? "outline" : "destructive"
                      }>
                        {selectedCampaign.status}
                      </Badge>
                    </div>
                    <div><span className="font-medium">Target Leads:</span> {selectedCampaign.leadCount}</div>
                    <div><span className="font-medium">Created:</span> {new Date(selectedCampaign.createdAt).toLocaleDateString()}</div>
                    <div><span className="font-medium">AI Personalization:</span> {selectedCampaign.aiPersonalization ? "Enabled" : "Disabled"}</div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-semibold mb-2">Campaign Stats</h3>
                  {selectedCampaign.stats ? (
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Emails Sent:</span> {selectedCampaign.stats.emailsSent}</div>
                      <div><span className="font-medium">Opens:</span> {selectedCampaign.stats.emailsOpened}</div>
                      <div><span className="font-medium">Clicks:</span> {selectedCampaign.stats.emailsClicked}</div>
                      <div><span className="font-medium">Open Rate:</span> {selectedCampaign.stats.openRate.toFixed(1)}%</div>
                      <div><span className="font-medium">Click Rate:</span> {selectedCampaign.stats.clickRate.toFixed(1)}%</div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No stats available yet</p>
                  )}
                </div>
              </div>

              {/* Email Content */}
              <div>
                <h3 className="font-semibold mb-2">Email Content</h3>
                <div className="space-y-4">
                  {selectedCampaign.customSubject && (
                    <div>
                      <Label className="font-medium">Subject:</Label>
                      <div className="mt-1 p-3 bg-muted rounded-md text-sm">
                        {selectedCampaign.customSubject}
                      </div>
                    </div>
                  )}
                  
                  {selectedCampaign.customContent && (
                    <div>
                      <Label className="font-medium">Content:</Label>
                      <div className="mt-1 p-3 bg-muted rounded-md text-sm whitespace-pre-wrap">
                        {selectedCampaign.customContent}
                      </div>
                    </div>
                  )}

                  {selectedCampaign.templateName && (
                    <div>
                      <Label className="font-medium">Template Used:</Label>
                      <div className="mt-1 p-2 bg-blue-50 rounded-md text-sm">
                        {selectedCampaign.templateName}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              {selectedCampaign.description && (
                <div>
                  <h3 className="font-semibold mb-2">Description</h3>
                  <p className="text-sm text-muted-foreground">{selectedCampaign.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
